import React, { useState, useEffect } from "react";
import "../../../css/ViewCampCenterModal.css";
import api from "../../../api/axiosConfig";
import ConfirmationModal from "./ConfirmationModal"; // <-- ADDED
import { showToast } from '../../Shared/Toaster';
import CitySelect from "../../Shared/CitySelect";


// ... (your CampCenter interface)
interface CampCenter {
  _id: string;
  name: string;
  centerType: string;
  city: string;
  createdAt: string;
  isActive: boolean;
  disabledOn?: string;
  addressLine1?: string;
  addressLine2?: string;
  telephone1?: string;
  telephone2?: string;
  latitude?: number;
  longitude?: number;
}

interface ViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  center: CampCenter | null;
  onUpdateSuccess: () => void; // ✅ Accept the new prop
}

const ViewCampCenterModal: React.FC<ViewModalProps> = ({
  isOpen,
  onClose,
  center,
  onUpdateSuccess,
}) => {
  // ✅ State to toggle between view and edit modes
  const [isEditing, setIsEditing] = useState(false);

  // <-- ADDED STATE -->
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [isDisabling, setIsDisabling] = useState(false);

  // ✅ State to hold the form data while editing
  const [formData, setFormData] = useState({
    addressLine1: "",
    addressLine2: "",
    city: "",
    telephone1: "",
    telephone2: "",
    latitude: 0,
    longitude: 0,
  });

  // ✅ Pre-fill form data when the modal opens or the center data changes
  useEffect(() => {
    if (center) {
      setFormData({
        addressLine1: center.addressLine1 || "",
        addressLine2: center.addressLine2 || "",
        city: center.city || "",
        telephone1: center.telephone1 || "",
        telephone2: center.telephone2 || "",
        latitude: center.latitude || 0,
        longitude: center.longitude || 0,
      });
    }
  }, [center]);

  // ✅ Handle changes in form inputs
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Handle the update API call
  const handleUpdate = async () => {
    if (!center) return;

    try {
      const token = sessionStorage.getItem("accessToken");
      await api.center_api.put(
        `/api/v1/centers/${center._id}`, // Your PUT endpoint
        formData, // The updated data
        {
          headers: { Authorization: `Bearer ${token}` }, // The auth header
        }
      );

      showToast("Center updated successfully!", 'success');
      onUpdateSuccess(); // Refresh the table in the parent component
      onClose(); // Close the modal
    } catch (error) {
      console.error("Failed to update center:", error);
      showToast("Error: Could not update center.", 'error');
    } finally {
      setIsEditing(false); // Exit edit mode
    }
  };

  // <-- ADDED: Handler for the disable/enable confirmation -->
  const handleDisableConfirm = async () => {
    if (!center) return;

    // Use !center.disabledOn to check if currently active
    // const currentlyActive = !center.disabledOn;

    setIsDisabling(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      if (!token) {
        showToast("Authentication error: No access token found.", 'error');
        setIsDisabling(false);
        return;
      }

      await api.center_api.patch(
        `/api/v1/centers/toggle-active`,
        { centerId: center._id }, // Send centerId in the body
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // showToast(`Center ${currentlyActive ? "disabled" : "enabled"} successfully!`, 'success');
      showToast(
        `Center ${center.isActive ? "disabled" : "enabled"} successfully!`
      , 'success');

      onUpdateSuccess(); // Call the success prop to refresh parent
      setShowDisableConfirm(false);
      onClose(); // Close the main modal after action
    } catch (error) {
      console.error("Failed to toggle center status:", error);
      showToast("Error: Could not update the center status. Please try again.", 'error');
    } finally {
      setIsDisabling(false);
    }
  };

  // ✅ Function to cancel editing
  const handleCancel = () => {
    setIsEditing(false);
    // Optional: reset formData to original `center` data if you want to discard changes
    if (center) {
      // This resets any typed changes when user hits cancel
      setFormData({
        addressLine1: center.addressLine1 || "",
        addressLine2: center.addressLine2 || "",
        city: center.city || "",
        telephone1: center.telephone1 || "",
        telephone2: center.telephone2 || "",
        latitude: center.latitude || 0,
        longitude: center.longitude || 0,
      });
    }
  };

  if (!isOpen || !center) {
    return null;
  }

  // Determine if active based on disabledOn property
  const isActive = !center.disabledOn;

  // Helper for rendering fields to avoid repetition
  const renderField = (
    label: string,
    name: keyof typeof formData,
    value: string | number
  ) => (
    <div className="view-camp-center-detail-item">
      <label>{label}</label>
      {isEditing ? (
        <input
          type="text"
          name={name}
          value={formData[name]}
          onChange={handleInputChange}
          className="edit-input"
        />
      ) : (
        <p>{value || "-"}</p>
      )}
    </div>
  );

  return (
    // <-- ADDED: React.Fragment wrapper -->
    <React.Fragment>
      <div className="view-camp-center-modal-backdrop" onClick={onClose}>
        <div className="view-camp-center-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #eee", paddingBottom: "1rem", marginBottom: "1.5rem" }}>
            <h2 style={{ margin: 0, borderBottom: "none", paddingBottom: 0 }}>{isEditing ? "Edit Camp Center" : "View Camp Center"}</h2>
            <button className="global-close-btn" onClick={onClose}>✕</button>
          </div>

          <div className="view-camp-center-modal-details">
            {/* Center Name is NOT editable */}
            <div className="view-camp-center-detail-item">
              <label>Center Name</label>
              <p>
                <b>{center.name}</b>
              </p>
            </div>
             {/* <-- ADDED: Status field --> */}
            <div className="view-camp-center-detail-item">
              <label>Status</label>
              <p>{isActive ? "Active" : "Disabled"}</p>
            </div>

            {renderField("Address 1", "addressLine1", center.addressLine1!)}
            {renderField("Address 2", "addressLine2", center.addressLine2!)}
            <div className="view-camp-center-detail-item">
              <label>City</label>
              {isEditing ? (
                <CitySelect
                  value={formData.city}
                  onChange={(val) => setFormData((prev) => ({ ...prev, city: val }))}
                  placeholder="Select city"
                />
              ) : (
                <p>{formData.city || "-"}</p>
              )}
            </div>
            {renderField("Telephone 1", "telephone1", center.telephone1!)}
            {renderField("Telephone 2", "telephone2", center.telephone2!)}
            {renderField("Latitude", "latitude", center.latitude!)}
            {renderField("Longitude", "longitude", center.longitude!)}
          </div>

          <div className="view-camp-center-modal-actions">
            {isEditing ? (
              <>
                <button
                  className="view-camp-center-btn btn-close"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button className="view-camp-center-btn btn-edit" onClick={handleUpdate}>
                  Update
                </button>
              </>
            ) : (
              <>
                <button
                  className="view-camp-center-btn btn-close"
                  onClick={onClose}
                >
                  Close
                </button>
                <button
                  className="view-camp-center-btn btn-edit"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </button>
                {/* --- MODIFIED BUTTON --- */}
                <button
                  className={`view-camp-center-btn ${
                    isActive ? "btn-disable" : "btn-edit" // Use disable style or edit style
                  }`}
                  onClick={() => setShowDisableConfirm(true)}
                  disabled={isDisabling}
                >
                  {isDisabling
                    ? center.isActive
                      ? "Disabling..."
                      : "Enabling..."
                    : center.isActive
                    ? "Disable"
                    : "Enable"}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* --- ADDED: Conditional Confirmation Modal --- */}
      {showDisableConfirm && (
        <ConfirmationModal
          message={`Are you sure you want to ${
            center.isActive ? "disable" : "enable"
          } this center?`}
          onConfirm={handleDisableConfirm}
          onCancel={() => setShowDisableConfirm(false)}
        />
      )}
    </React.Fragment>
  );
};

export default ViewCampCenterModal;